<script>
  import { onMount } from 'svelte';
  import Layout from '../components/Layout.svelte';
  import CustomerModal from '../components/CustomerModal.svelte';
  import ConfirmModal from '../components/ConfirmModal.svelte';
  import { customersApi } from '../stores/api.js';
  import { _ } from '../lib/i18n.js';
  import Users from 'lucide-svelte/icons/users';
  import { Plus, Search, Edit2, Trash2, ChevronLeft, ChevronRight, Eye } from 'lucide-svelte';
  import { push } from 'svelte-spa-router';

  let customers = [];
  let loading = false;
  let error = '';
  let searchTerm = '';
  let currentPage = 1;
  let totalPages = 0;
  let totalCustomers = 0;
  const itemsPerPage = 10;

  // Modal states
  let showCustomerModal = false;
  let showConfirmModal = false;
  let editingCustomer = null;
  let customerToDelete = null;
  // Save failures go to the open modal, not the page banner hidden behind it.
  let modalError = null;

  // Search debounce
  let searchTimeout;

  onMount(() => {
    loadCustomers();
  });

  async function loadCustomers() {
    try {
      loading = true;
      error = '';

      const params = {
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm.trim(),
        sortBy: 'companyName',
        sortOrder: 'asc'
      };

      const response = await customersApi.getAll(params);
      customers = response.customers || [];
      totalCustomers = response.pagination.total;
      totalPages = response.pagination.pages;

    } catch (err) {
      error = err.message;
      console.error('Load customers error:', err);
    } finally {
      loading = false;
    }
  }

  function handleSearch() {
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
      currentPage = 1;
      loadCustomers();
    }, 300);
  }

  function handlePageChange(newPage) {
    if (newPage >= 1 && newPage <= totalPages) {
      currentPage = newPage;
      loadCustomers();
    }
  }

  function openAddModal() {
    editingCustomer = null;
    modalError = null;
    showCustomerModal = true;
  }

  function openEditModal(customer) {
    editingCustomer = customer;
    modalError = null;
    showCustomerModal = true;
  }

  function closeCustomerModal() {
    showCustomerModal = false;
    modalError = null;
  }

  function openDeleteModal(customer) {
    customerToDelete = customer;
    showConfirmModal = true;
  }

  async function handleCustomerSaved(event) {
    const { customer, isEdit } = event.detail;
    modalError = null;

    try {
      if (isEdit) {
        await customersApi.update(customer.id, customer);
      } else {
        await customersApi.create(customer);
      }
    } catch (err) {
      // Keep the modal open so the user can correct the data and retry.
      modalError = { status: err.status, message: err.message };
      return;
    }

    closeCustomerModal();
    await loadCustomers();
  }

  async function handleDeleteConfirmed() {
    if (!customerToDelete) return;

    try {
      await customersApi.delete(customerToDelete.id);
      showConfirmModal = false;
      customerToDelete = null;
      await loadCustomers();

    } catch (err) {
      error = err.message;
      showConfirmModal = false;
    }
  }

  function handleDeleteCancelled() {
    showConfirmModal = false;
    customerToDelete = null;
  }

  function getDisplayName(customer) {
    if (customer.companyName) {
      return customer.companyName;
    }
    return [customer.name, customer.surname].filter(Boolean).join(' ') || customer.id;
  }

  function viewCustomer(customer) {
    push(`/customers/${customer.id}`);
  }
</script>

<Layout>
  <div class="max-w-7xl mx-auto">
    <!-- Header -->
    <div class="page-header">
      <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
        <div class="space-y-3">
          <div class="flex items-center space-x-3">
            <div class="w-12 h-12 bg-linear-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center shadow-lg">
              <Users size={24} class="text-white" />
            </div>
            <div>
              <h1 class="text-3xl font-bold text-gray-900 dark:text-gray-100">{$_('customers.title')}</h1>
              <p class="text-gray-600 dark:text-gray-400 font-medium">{$_('customers.subtitle')}</p>
            </div>
          </div>
          <div class="flex items-center space-x-4 text-sm">
            <div class="flex items-center space-x-2 bg-primary-50 dark:bg-primary-900/30 px-3 py-1 rounded-full">
              <div class="w-2 h-2 bg-primary-500 rounded-full animate-pulse"></div>
              <span class="text-primary-700 dark:text-primary-400 font-semibold">{$_('customers.totalCount', { values: { count: totalCustomers } })}</span>
            </div>
            {#if searchTerm}
              <div class="flex items-center space-x-2 bg-blue-50 dark:bg-blue-900/30 px-3 py-1 rounded-full">
                <Search size={12} class="text-blue-500 dark:text-blue-400" />
                <span class="text-blue-700 dark:text-blue-400 font-medium">{$_('common.filtered')}: {customers.length}</span>
              </div>
            {/if}
          </div>
        </div>
        <button
          on:click={openAddModal}
          class="btn btn-primary shadow-primary transform hover:scale-105"
        >
          <Plus size={18} class="mr-2" />
          <span class="font-semibold">{$_('customers.add')}</span>
        </button>
      </div>
    </div>

    <!-- Search and Filters -->
    <div class="card p-6 mb-8">
      <div class="flex flex-col lg:flex-row gap-6">
        <div class="flex-1">
          <label class="label mb-3">
            <div class="flex items-center space-x-2">
              <Search size={16} class="text-primary-500" />
              <span>{$_('customers.searchLabel')}</span>
            </div>
          </label>
          <div class="relative group">
            <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <Search class="h-5 w-5 text-gray-400 group-focus-within:text-primary-500 transition-colors" />
            </div>
            <input
              type="text"
              bind:value={searchTerm}
              on:input={handleSearch}
              placeholder={$_('customers.searchPlaceholder')}
              class="input pl-12 pr-4 h-12 text-base border-gradient"
            />
            {#if searchTerm}
              <button
                on:click={() => { searchTerm = ''; handleSearch(); }}
                class="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            {/if}
          </div>
        </div>
        <div class="flex items-end space-x-3">
          <button class="btn btn-secondary h-12">
            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            {$_('common.filters')}
          </button>
        </div>
      </div>
    </div>

    <!-- Error Message -->
    {#if error}
      <div class="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-md p-4 mb-6">
        <div class="text-sm text-red-800 dark:text-red-300">{error}</div>
      </div>
    {/if}

    <!-- Customers Table -->
    <div class="card-elevated overflow-hidden">
      {#if loading}
        <div class="p-12 text-center">
          <div class="loading-spinner h-12 w-12 mx-auto mb-4"></div>
          <div class="space-y-2">
            <p class="text-lg font-medium text-gray-700 dark:text-gray-300">{$_('common.loading')}</p>
            <p class="text-sm text-gray-500 dark:text-gray-400">{$_('customers.loadingMessage')}</p>
          </div>
        </div>
      {:else if customers.length === 0}
        <div class="p-12 text-center">
          <div class="mx-auto w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
            <Users size={32} class="text-gray-400 dark:text-gray-500" />
          </div>
          <div class="space-y-2">
            <h3 class="text-lg font-medium text-gray-900 dark:text-gray-100">
              {searchTerm ? $_('customers.noResults') : $_('customers.noCustomers')}
            </h3>
            <p class="text-gray-500 dark:text-gray-400 max-w-sm mx-auto">
              {searchTerm ? $_('customers.noResultsHint') : $_('customers.noCustomersHint')}
            </p>
            {#if !searchTerm}
              <button
                on:click={openAddModal}
                class="mt-4 btn btn-primary"
              >
                <Plus size={16} class="mr-2" />
                {$_('customers.addFirstCustomer')}
              </button>
            {/if}
          </div>
        </div>
      {:else}
        <div class="overflow-x-auto">
          <table class="table-modern">
            <thead class="table-header">
              <tr>
                <th class="table-cell text-left text-xs font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                  <div class="flex items-center space-x-2">
                    <span>{$_('customers.fields.id')}</span>
                  </div>
                </th>
                <th class="table-cell text-left text-xs font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                  <div class="flex items-center space-x-2">
                    <span>{$_('customers.customer')}</span>
                  </div>
                </th>
                <th class="table-cell text-left text-xs font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                  <div class="flex items-center space-x-2">
                    <span>{$_('customers.contacts')}</span>
                  </div>
                </th>
                <th class="table-cell text-left text-xs font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                  <div class="flex items-center space-x-2">
                    <span>{$_('customers.vatNumber')}</span>
                  </div>
                </th>
                <th class="table-cell text-left text-xs font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                  <div class="flex items-center space-x-2">
                    <span>{$_('customers.location')}</span>
                  </div>
                </th>
                <th class="table-cell text-right text-xs font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                  <span>{$_('common.actions')}</span>
                </th>
              </tr>
            </thead>
            <tbody class="bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm divide-y divide-gray-200/60 dark:divide-gray-700/60">
              {#each customers as customer, index}
                <tr class="table-row group">
                  <td class="table-cell">
                    <div class="flex items-center space-x-3">
                      <div class="w-8 h-8 bg-linear-to-br from-primary-100 to-primary-200 dark:from-primary-900/50 dark:to-primary-800/50 rounded-lg flex items-center justify-center text-primary-700 dark:text-primary-400 font-bold text-sm">
                        {customer.id.charAt(0)}
                      </div>
                      <span class="font-mono text-sm font-semibold text-gray-900 dark:text-gray-100">{customer.id}</span>
                    </div>
                  </td>
                  <td class="table-cell">
                    <div class="space-y-1">
                      <div class="font-semibold text-gray-900 dark:text-gray-100 text-sm">
                        {getDisplayName(customer)}
                      </div>
                      {#if customer.type}
                        <div class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                          {customer.type}
                        </div>
                      {/if}
                    </div>
                  </td>
                  <td class="table-cell">
                    <div class="space-y-1">
                      {#if customer.email}
                        <div class="flex items-center space-x-2">
                          <svg class="w-3 h-3 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                          </svg>
                          <span class="text-sm text-gray-600 dark:text-gray-400 truncate max-w-[200px]">{customer.email}</span>
                        </div>
                      {/if}
                      {#if customer.phone}
                        <div class="flex items-center space-x-2">
                          <svg class="w-3 h-3 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                          </svg>
                          <span class="text-sm text-gray-600 dark:text-gray-400">{customer.phone}</span>
                        </div>
                      {/if}
                      {#if !customer.email && !customer.phone}
                        <span class="text-gray-400 dark:text-gray-500 text-sm italic">{$_('common.noContact')}</span>
                      {/if}
                    </div>
                  </td>
                  <td class="table-cell">
                    {#if customer.VATNumber}
                      <div class="font-mono text-sm font-medium text-gray-900 dark:text-gray-100">{customer.VATNumber}</div>
                    {:else}
                      <span class="text-gray-400 dark:text-gray-500 text-sm">-</span>
                    {/if}
                  </td>
                  <td class="table-cell">
                    <div class="space-y-1">
                      {#if customer.country}
                        <div class="flex items-center space-x-2">
                          <svg class="w-3 h-3 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <span class="text-sm text-gray-900 dark:text-gray-100">{customer.country}</span>
                        </div>
                        {#if customer.province}
                          <span class="text-xs text-gray-500 dark:text-gray-400">{customer.province}</span>
                        {/if}
                      {:else}
                        <span class="text-gray-400 dark:text-gray-500 text-sm">-</span>
                      {/if}
                    </div>
                  </td>
                  <td class="table-cell text-right">
                    <div class="flex justify-end items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      <button
                        on:click={() => viewCustomer(customer)}
                        class="p-2 text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-all duration-200 transform hover:scale-110"
                        title={$_('common.view')}
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        on:click={() => openEditModal(customer)}
                        class="p-2 text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded-lg transition-all duration-200 transform hover:scale-110"
                        title={$_('common.edit')}
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        on:click={() => openDeleteModal(customer)}
                        class="p-2 text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-all duration-200 transform hover:scale-110"
                        title={$_('common.delete')}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        {#if totalPages > 1}
          <div class="bg-white dark:bg-gray-900 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 sm:px-6">
            <div class="flex-1 flex justify-between sm:hidden">
              <button
                on:click={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                class="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {$_('common.previous')}
              </button>
              <button
                on:click={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                class="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {$_('common.next')}
              </button>
            </div>
            <div class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p class="text-sm text-gray-700 dark:text-gray-300">
                  {$_('common.page')} <span class="font-medium">{currentPage}</span> {$_('common.of')}
                  <span class="font-medium">{totalPages}</span>
                </p>
              </div>
              <div>
                <nav class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    on:click={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    class="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <button
                    on:click={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    class="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight size={16} />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        {/if}
      {/if}
    </div>
  </div>
</Layout>

<!-- Customer Modal -->
{#if showCustomerModal}
  <CustomerModal
    customer={editingCustomer}
    serverError={modalError}
    on:save={handleCustomerSaved}
    on:cancel={closeCustomerModal}
  />
{/if}

<!-- Confirm Delete Modal -->
{#if showConfirmModal}
  <ConfirmModal
    title={$_('customers.delete')}
    message={$_('customers.deleteConfirm')}
    confirmText={$_('common.delete')}
    confirmClass="btn-danger"
    on:confirm={handleDeleteConfirmed}
    on:cancel={handleDeleteCancelled}
  />
{/if}
